import React, { useState, useCallback, useEffect } from "react";
import { MasterTable, AutoComplete, DatePicker as CommonDatePicker } from "../../components/_index";
import { DatePicker as AntdDatePicker } from "antd";
import dayjs from "dayjs";
import { getTask, updateTask } from "../../services/task.service";
import { Box, Button, Stack, Typography, Chip } from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { debounce } from "../../utils/debounce";
import { useDispatch } from "react-redux";
import { getRole } from "../../services/category.service";
import { getUser, getHrAdminAccountsUsers } from "../../services/user.service";
import moment from "moment";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import UndoIcon from "@mui/icons-material/Undo";
import CompleteTaskModal from "./CompleteTaskModal";
import { showToast } from "../../redux/Reducer/toastReducer";
import ConfirmBox from "../../components/ConfirmBox/ConfirmBox";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TaskHistory from "./TaskHistory";

const { RangePicker } = AntdDatePicker;

const Task = () => {
    const [data, setData] = useState([]);
    const [tableTotalCount, setTableCount] = useState(0);
    const dispatch = useDispatch();
    const [queryParam, setQueryParam] = useState({
        search: "",
        page: 1,
        limit: 10,
        sortType: "",
        sort: "",
        departmentId: "",
        inchargeId: "",
        startDate: moment().startOf('month').format("YYYY-MM-DD"),
        endDate: moment().endOf('month').format("YYYY-MM-DD"),
        isTask: true,
        urgencyStatus: "",
    });

    const [departmentData, setDepartmentData] = useState([]);
    const [usersData, setUsersData] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [selectedIncharge, setSelectedIncharge] = useState(null);
    const [selectedRange, setSelectedRange] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
    const [openCompleteModal, setOpenCompleteModal] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [confirmRevertOpen, setConfirmRevertOpen] = useState(false);
    const [taskToRevert, setTaskToRevert] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [openHistory, setOpenHistory] = useState(false);
    const [selectedTaskId, setSelectedTaskId] = useState(null);
    const [selectedCompletionFilter, setSelectedCompletionFilter] = useState(null);

    const statusOptions = [
        { label: "Overdue", value: "red", _id: "red" },
        { label: "upcoming", value: "yellow", _id: "yellow" },
        { label: "On Track", value: "green", _id: "green" }
    ];

    const completionOptions = [
        { label: "All Tasks", value: "all", _id: "all" },
        { label: "Overdue", value: "red", _id: "red" },
        { label: "Upcoming", value: "yellow", _id: "yellow" },
        { label: "On Track", value: "green", _id: "green" },
        { label: "Completed", value: "completed", _id: "completed" }
    ];

    const calculateExtraDays = (targetDate, completedDate) => {
        const target = moment(targetDate).startOf('day');

        if (completedDate) {
            const completed = moment(completedDate).startOf('day');
            const diff = completed.diff(target, "days");
            if (diff > 0) return `+${diff} days`;
            if (diff < 0) return `completed before ${Math.abs(diff)} days`;
            return "On Time";
        } else {
            const today = moment().startOf('day');
            if (target.isBefore(today)) {
                const diff = target.diff(today, "days");
                return `-${Math.abs(diff)} days`;
            }
            return "-";
        }
    };

    const getStatusColor = (targetDate, completedDate) => {
        if (completedDate) {
            // Task is completed
            const target = moment(targetDate).startOf('day');
            const completed = moment(completedDate).startOf('day');
            const diff = completed.diff(target, "days");
            return diff > 0 ? "red" : "green"; // Red if late, green if on time or early
        } else {
            // Task is not completed yet
            const target = moment(targetDate).startOf('day');
            const today = moment().startOf('day');
            const daysUntilDue = target.diff(today, "days");

            if (daysUntilDue < 0) return "red"; // Overdue: Target is before Today
            if (daysUntilDue <= 2) return "yellow"; // Target is Today, Today+1, or Today+2
            return "green"; // Target is more than 2 days in the future
        }
    };

    const GetTaskData = async () => {
        try {
            const res = await getTask(queryParam);
            console.log(res, "Task Data");
            let modifiedData =
                res?.data.map((e) => {
                    const extraDaysStr = calculateExtraDays(e.targetDateToComplete, e.completedDate);
                    const color = getStatusColor(e.targetDateToComplete, e.completedDate);
                    return {
                        ...e,
                        Department: e.departmentName || "-",
                        incharge: e.inchargeName || "-",
                        targetDateToComplete: e.targetDateToComplete ? moment(e.targetDateToComplete).format("DD-MM-YYYY") : "-",
                        completedDate: e.completedDate ? moment(e.completedDate).format("DD-MM-YYYY") : "-",
                        extraDays: extraDaysStr,
                        statusColor: color,
                    };
                }) || [];

            // Filter based on urgency status and completion
            if (selectedCompletionFilter?.value === "completed") {
                modifiedData = modifiedData.filter(item => item.completedDate && item.completedDate !== "-");
            } else if (selectedCompletionFilter?.value && selectedCompletionFilter?.value !== "all") {
                modifiedData = modifiedData.filter(item => item.statusColor === selectedCompletionFilter.value && (!item.completedDate || item.completedDate === "-"));
            }
            // If "All Tasks" is selected or no filter is selected, show all pending tasks (hide completed tasks)
            else {
                modifiedData = modifiedData.filter(item => !item.completedDate || item.completedDate === "-");
            }

            setTableCount(res?.totalCount || 0);
            setData(modifiedData);
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        GetTaskData();
    }, [
        queryParam.search,
        queryParam.sort,
        queryParam.limit,
        queryParam.page,
        queryParam.departmentId,
        queryParam.inchargeId,
        queryParam.urgencyStatus,
        queryParam.startDate,
        queryParam.endDate,
        selectedCompletionFilter,
    ]);

    useEffect(() => {
        getDepartments();
        getUsers();
    }, []);

    const getDepartments = async () => {
        try {
            const res = await getRole({ isHrAdminWork: true });
            if (res?.data) {
                const modData = res.data.map(d => ({ ...d, label: d.name, id: d._id }));
                setDepartmentData(modData);
            }
        } catch (error) {
            console.log(error);
        }
    };


    const getUsers = async () => {
        try {
            const res = await getHrAdminAccountsUsers();
            if (res?.data) {
                const modData = res.data.map(u => ({ ...u, label: u.name, id: u._id }));
                setUsersData(modData);
            }
        } catch (error) {
            console.log(error);
        }
    };

    const columns = [
        { id: "taskNo", label: "Task No" },
        { id: "task", label: "Task" },
        { id: "Department", label: "Department" },
        { id: "incharge", label: "Incharge" },
        { id: "targetDateToComplete", label: "Target Date" },
        { id: "completedDate", label: "Completed Date" },
        { id: "extraDays", label: "Extra Day Taken to Complete" },
    ];

    const tableOutputEvent = useCallback((params) => {
        setQueryParam((queryParams) => ({
            ...queryParams,
            ...params,
        }));
    }, []);

    const debouncedHandleSearch = useCallback(
        debounce((searchText) => {
            setQueryParam((queryParams) => ({
                ...queryParams,
                search: searchText,
            }));
        }, 300),
        []
    );

    const handleSearch = (searchText) => {
        debouncedHandleSearch(searchText);
    };

    // Custom row rendering to add status color to font
    const customRowRender = (row) => {
        let color = "#4d4d4d"; // Default color

        // If task is completed, show black color
        if (row.completedDate && row.completedDate !== "-") {
            color = "#000000";
        } else {
            // Otherwise use status color
            if (row.statusColor === "red") color = "#d32f2f";
            else if (row.statusColor === "yellow") color = "#ed6c02"; // Using orange for better visibility than yellow
            else if (row.statusColor === "green") color = "#2e7d32";
        }

        return {
            sx: {
                backgroundColor: "white !important",
                "& .MuiTypography-root": {
                    color: `${color} !important`,
                    fontWeight: "500"
                },
                "& .MuiTableCell-root": {
                    color: `${color} !important`
                }
            }
        };
    };

    const handleComplete = (row) => {
        if (row.completedDate && row.completedDate !== "-") return;
        setSelectedTask(row);
        setOpenCompleteModal(true);
    };

    const handleRevert = (row) => {
        setTaskToRevert(row);
        setConfirmRevertOpen(true);
    };

    const handleViewHistory = (row) => {
        setSelectedTaskId(row._id);
        setOpenHistory(true);
    };

    const confirmRevert = async () => {
        try {
            const payload = {
                completedDate: null,
                status: "1", // Revert to active/pending status
                remarks: ""
            };
            await updateTask(taskToRevert._id, payload);
            dispatch(showToast({ message: "Task reverted successfully", type: "success" }));
            setConfirmRevertOpen(false);
            GetTaskData();
        } catch (error) {
            dispatch(showToast({ message: "Failed to revert task", type: "error" }));
        }
    }

    const tableActionButtons = [
        {
            title: "Complete Task",
            btnIcon: <CheckCircleOutlineIcon sx={{ color: "green" }} />,
            onClick: handleComplete,
            shouldRender: (row) => row.status !== "2"
        },
        {
            title: "Revert Task",
            btnIcon: <UndoIcon sx={{ color: "orange" }} />,
            onClick: handleRevert,
            shouldRender: (row) => row.status === "2"
        },
        {
            title: "View History",
            btnIcon: <VisibilityIcon sx={{ color: "black" }} />,
            onClick: handleViewHistory,
            shouldRender: () => true
        }
    ];

    return (
        <div className="mainContent">
            <Box sx={{ display: "flex", justifyContent: "space-between" }} mb={2}>
                <Stack
                    direction={"row"}
                    alignItems={"center"}
                    spacing={2}
                >
                    <Stack sx={{ width: "180px" }}>
                        <Box className="SearchInputBoxContainer">
                            <SearchOutlinedIcon className="Search_Icon" />
                            <input
                                className="SearchInputBox"
                                placeholder="Search here"
                                onChange={(e) => handleSearch(e.target.value)}
                            />
                        </Box>
                    </Stack>

                    <Stack sx={{ width: "180px" }}>
                        <AutoComplete
                            options={departmentData}
                            value={selectedDepartment}
                            getOptionLabel={(option) => option.label}
                            onChange={(e, v) => {
                                setSelectedDepartment(v);
                                setQueryParam(prev => ({ ...prev, departmentId: v?.id || "", page: 1 }));
                            }}
                            placeholder="Select Department"
                        />
                    </Stack>

                    <Stack sx={{ width: "180px" }}>
                        <AutoComplete
                            options={usersData}
                            value={selectedIncharge}
                            getOptionLabel={(option) => option.label}
                            onChange={(e, v) => {
                                setSelectedIncharge(v);
                                setQueryParam(prev => ({ ...prev, inchargeId: v?.id || "", page: 1 }));
                            }}
                            placeholder="Select Incharge"
                        />
                    </Stack>

                    <Stack sx={{ width: "250px" }}>
                        <RangePicker
                            value={selectedRange}
                            onChange={(dates, dateStrings) => {
                                setSelectedRange(dates);
                                setQueryParam(prev => ({
                                    ...prev,
                                    startDate: dateStrings[0] || "",
                                    endDate: dateStrings[1] || "",
                                    page: 1
                                }));
                            }}
                            format="YYYY-MM-DD"
                            style={{ height: "40px", borderRadius: "8px", width: "100%" }}
                        />
                    </Stack>

                    {/* <Stack sx={{ width: "180px" }}>
                        <AutoComplete
                            options={statusOptions}
                            value={selectedStatus}
                            getOptionLabel={(option) => option.label}
                            onChange={(e, v) => {
                                setSelectedStatus(v);
                                setQueryParam(prev => ({
                                    ...prev,
                                    urgencyStatus: v?.value || "",
                                    page: 1
                                }));
                            }}
                            placeholder="Select Status"
                        />
                    </Stack> */}

                    <Stack sx={{ width: "180px" }}>
                        <AutoComplete
                            options={completionOptions}
                            value={selectedCompletionFilter}
                            getOptionLabel={(option) => option.label}
                            onChange={(e, v) => {
                                setSelectedCompletionFilter(v);
                            }}
                            placeholder="Filter by Status"
                        />
                    </Stack>
                </Stack>
            </Box>
            <MasterTable
                data={data}
                columns={columns}
                isServerSide={true}
                tableTotalCount={tableTotalCount}

                tableOutputEvent={tableOutputEvent}
                customRowStyle={customRowRender}
                tableActionButton={tableActionButtons}
            />
            {openCompleteModal && (
                <CompleteTaskModal
                    open={openCompleteModal}
                    handleClose={() => setOpenCompleteModal(false)}
                    selectedData={selectedTask}
                    refreshData={GetTaskData}
                />

            )}
            <ConfirmBox
                open={confirmRevertOpen}
                title="Revert Task"
                content="Are you sure you want to revert this task status to pending?"
                confirmButton="Revert"
                setOpenConfirmBox={() => setConfirmRevertOpen(false)}
                Function={confirmRevert}
            />
            {openHistory && (
                <TaskHistory
                    open={openHistory}
                    handleClose={() => setOpenHistory(false)}
                    taskId={selectedTaskId}
                />
            )}
        </div>
    );
};

export default Task;
